import React from 'react';

/**
 * Translate FIRMS fire radiative power (FRP, in MW) into a human-readable
 * intensity label.
 */
export function intensityLabel(frp) {
  const value = frp == null || frp === '' ? null : parseFloat(frp);
  if (value == null || Number.isNaN(value)) return 'Unknown';
  if (value >= 100) return 'Extreme';
  if (value >= 50)  return 'Very High';
  if (value >= 10)  return 'High';
  if (value >= 1)   return 'Moderate';
  return 'Low';
}

/**
 * Normalize FIRMS confidence into a readable label and numeric range hint.
 */
export function confidenceLabel(confidence) {
  if (confidence == null || confidence === '') return 'Unknown';
  const str = String(confidence).trim().toLowerCase();

  if (str === 'high' || str === 'h') return 'High confidence';
  if (str === 'nominal' || str === 'n') return 'Nominal confidence';
  if (str === 'low' || str === 'l') return 'Low confidence';

  const num = parseFloat(str);
  if (!Number.isNaN(num)) {
    if (num >= 70) return `High confidence (${num})`;
    if (num >= 30) return `Nominal confidence (${num})`;
    return `Low confidence (${num})`;
  }

  return String(confidence);
}

export function formatAcquiredAt(acquiredAt) {
  if (!acquiredAt) return null;
  try {
    const date = new Date(acquiredAt);
    if (Number.isNaN(date.getTime())) return acquiredAt;
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return acquiredAt;
  }
}

/**
 * Tooltip content for a NASA FIRMS active fire detection marker.
 *
 * @param {Object} props
 * @param {Object} props.properties - FIRMS feature properties.
 */
function FireTooltip({ properties = {} }) {
  const p = properties;
  const acquiredAt = p.acquiredAt ?? (p.acq_date && p.acq_time
    ? `${p.acq_date} ${p.acq_time.slice(0, 2)}:${p.acq_time.slice(2, 4)} UTC`
    : null);

  return (
    <div>
      <strong>🔥 Active fire detection</strong>
      {acquiredAt && (
        <><br /><span>Detected: {formatAcquiredAt(acquiredAt)}</span></>
      )}
      {p.frp != null && (
        <><br /><span>Intensity: {intensityLabel(p.frp)} ({Number(p.frp).toFixed(1)} MW)</span></>
      )}
      {p.confidence != null && (
        <><br /><span>{confidenceLabel(p.confidence)}</span></>
      )}
      {(p.satellite || p.instrument) && (
        <><br /><span>Source: {[p.satellite, p.instrument].filter(Boolean).join(' / ')}</span></>
      )}
    </div>
  );
}

export default FireTooltip;
